/**
 * Builder RFC 822 minimal pour `users.messages.send`.
 *
 * Construit un message multipart/alternative (text + html) avec headers
 * From/To/Cc/Bcc/Subject/Date/Message-ID + threading (In-Reply-To, References).
 *
 * - Subject encodé RFC 2047 si non-ASCII
 * - Body parts encodés base64 avec line breaks RFC 2045
 * - Output final encodé base64url pour Gmail API `raw` field
 */

export interface MessageDraft {
  to: string[];                 // emails (peut inclure "Nom <email>")
  cc?: string[];
  bcc?: string[];
  subject: string;
  /** Texte plain saisi par l'user dans le textarea */
  bodyText: string;
  /** Signature HTML à appender (optionnel) */
  signatureHtml?: string;
  /** HTML de citation (pour les replies) — append après signature */
  quoteHtml?: string;
  /** Plain text version de la citation pour le text/plain part */
  quoteText?: string;
  /** Headers de threading (replies) */
  inReplyTo?: string;
  references?: string[];
  /** From override (sinon Gmail utilise l'authenticated user) */
  from?: string;
  /** Tracking : si fourni, injecte un pixel GET au end du HTML body */
  trackingPixelUrl?: string;
  /** Attachments à joindre — wrap automatique en multipart/mixed si présent */
  attachments?: Array<{
    filename: string;
    mimeType: string;
    bytes: Uint8Array<ArrayBuffer>;
  }>;
}

// ─── Encoding helpers ───────────────────────────────────────────────────────

/**
 * Encode UTF-8 → string Latin-1 (chaque char = 1 byte). Nécessaire pour btoa
 * qui n'accepte que Latin-1.
 */
function utf8ToBinaryString(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return bin;
}

/** Base64 standard (avec padding). */
function base64(s: string): string {
  return btoa(utf8ToBinaryString(s));
}

/** Base64url (Gmail API format) : - / _ au lieu de + /, pas de padding. */
export function base64UrlEncodeString(s: string): string {
  return base64(s)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/** Base64 avec line breaks tous les 76 chars (RFC 2045). */
function base64WithBreaks(s: string): string {
  const b = base64(s);
  return b.match(/.{1,76}/g)?.join("\r\n") ?? "";
}

/** Base64 from raw bytes (chunked pour grosses pièces jointes). */
function bytesToBase64WithBreaks(bytes: Uint8Array<ArrayBuffer>): string {
  const chunkSize = 0x8000; // 32k chars par chunk pour éviter call stack overflow
  let bin = "";
  for (let i = 0; i < bytes.length; i += chunkSize) {
    bin += String.fromCharCode.apply(
      null,
      Array.from(bytes.subarray(i, i + chunkSize)),
    );
  }
  return btoa(bin).match(/.{1,76}/g)?.join("\r\n") ?? "";
}

/**
 * Encode un header value en RFC 2047 si non-ASCII (B encoding).
 * Sinon retourne tel quel.
 */
function encodeHeader(s: string): string {
  // Pure ASCII → no encoding needed
  // eslint-disable-next-line no-control-regex
  if (/^[\x00-\x7F]*$/.test(s)) return s;
  return `=?UTF-8?B?${base64(s)}?=`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    const map: Record<string, string> = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    };
    return map[c] ?? c;
  });
}

/** Convertit du plain text en HTML basique avec line breaks. */
export function plainToHtml(plain: string): string {
  return plain
    .split(/\r?\n/)
    .map((line) => escapeHtml(line) || "<br>")
    .join("<br>\n");
}

function generateBoundary(): string {
  const random = Math.random().toString(36).slice(2, 10);
  const time = Date.now().toString(36);
  return `mfx_${time}_${random}`;
}

function generateMessageId(domain: string): string {
  const random = Math.random().toString(36).slice(2, 14);
  const time = Date.now().toString(36);
  return `<${time}.${random}@${domain}>`;
}

// ─── RFC 822 build ──────────────────────────────────────────────────────────

/**
 * Construit le message RFC 822 complet.
 * Le caller appellera `base64UrlEncodeString(result)` avant de POST.
 *
 * Sans attachments : `multipart/alternative` avec text + html parts.
 * Avec attachments : `multipart/mixed` qui wrap le multipart/alternative + 1
 * part par fichier (Content-Disposition: attachment).
 */
export function buildMessage(draft: MessageDraft, fromEmail: string): string {
  const altBoundary = generateBoundary();
  const hasAttachments = (draft.attachments?.length ?? 0) > 0;
  const mixedBoundary = hasAttachments ? generateBoundary() : "";
  const fromAddr = draft.from || fromEmail;
  const domain = fromEmail.split("@")[1] || "mailfox.local";
  const messageId = generateMessageId(domain);

  // === HTML body ============================================================
  const userBodyHtml = `<div>${plainToHtml(draft.bodyText)}</div>`;
  const sigHtml = draft.signatureHtml
    ? `<br><div class="mfx-sig">${draft.signatureHtml}</div>`
    : "";
  const quoteHtml = draft.quoteHtml ? `<br>${draft.quoteHtml}` : "";
  // Pixel tracking — placé en TOUTE FIN, après citation, pour qu'il soit chargé
  // quand l'user scroll au bout du mail (pas avant). 1×1, alt vide, hors flux.
  const pixelHtml = draft.trackingPixelUrl
    ? `<img src="${draft.trackingPixelUrl}" width="1" height="1" alt="" style="display:block;border:0;width:1px;height:1px;" />`
    : "";
  const fullHtml = `<!DOCTYPE html><html><body>${userBodyHtml}${sigHtml}${quoteHtml}${pixelHtml}</body></html>`;

  // === Plain text body ======================================================
  const sigText = draft.signatureHtml
    ? `\n\n--\n${stripHtmlToText(draft.signatureHtml)}`
    : "";
  const quoteText = draft.quoteText ? `\n\n${draft.quoteText}` : "";
  const fullText = draft.bodyText + sigText + quoteText;

  // === Headers ==============================================================
  const headers: string[] = [];
  headers.push(`From: ${fromAddr}`);
  headers.push(`To: ${draft.to.map(encodeHeader).join(", ")}`);
  if (draft.cc?.length) headers.push(`Cc: ${draft.cc.map(encodeHeader).join(", ")}`);
  if (draft.bcc?.length) headers.push(`Bcc: ${draft.bcc.map(encodeHeader).join(", ")}`);
  headers.push(`Subject: ${encodeHeader(draft.subject)}`);
  headers.push(`Date: ${rfc822Date(new Date())}`);
  headers.push(`Message-ID: ${messageId}`);
  if (draft.inReplyTo) headers.push(`In-Reply-To: ${draft.inReplyTo}`);
  if (draft.references?.length) {
    headers.push(`References: ${draft.references.join(" ")}`);
  }
  headers.push(`MIME-Version: 1.0`);
  if (hasAttachments) {
    headers.push(`Content-Type: multipart/mixed; boundary="${mixedBoundary}"`);
  } else {
    headers.push(`Content-Type: multipart/alternative; boundary="${altBoundary}"`);
  }

  // === Body parts (multipart/alternative inner) ==========================
  const partText = [
    `--${altBoundary}`,
    `Content-Type: text/plain; charset=UTF-8`,
    `Content-Transfer-Encoding: base64`,
    "",
    base64WithBreaks(fullText),
  ].join("\r\n");

  const partHtml = [
    `--${altBoundary}`,
    `Content-Type: text/html; charset=UTF-8`,
    `Content-Transfer-Encoding: base64`,
    "",
    base64WithBreaks(fullHtml),
  ].join("\r\n");

  if (!hasAttachments) {
    return [
      headers.join("\r\n"),
      "",
      partText,
      partHtml,
      `--${altBoundary}--`,
      "",
    ].join("\r\n");
  }

  // === Avec attachments : multipart/mixed wrapping ========================
  const altBlock = [
    `--${mixedBoundary}`,
    `Content-Type: multipart/alternative; boundary="${altBoundary}"`,
    "",
    partText,
    partHtml,
    `--${altBoundary}--`,
  ].join("\r\n");

  const attachmentParts = (draft.attachments ?? []).map((a) => {
    return [
      `--${mixedBoundary}`,
      `Content-Type: ${a.mimeType}; name="${a.filename.replace(/"/g, "")}"`,
      `Content-Disposition: attachment; filename="${a.filename.replace(/"/g, "")}"`,
      `Content-Transfer-Encoding: base64`,
      "",
      bytesToBase64WithBreaks(a.bytes),
    ].join("\r\n");
  });

  return [
    headers.join("\r\n"),
    "",
    altBlock,
    ...attachmentParts,
    `--${mixedBoundary}--`,
    "",
  ].join("\r\n");
}

/** Date au format RFC 822 : "Fri, 26 Apr 2026 09:30:00 +0000" */
function rfc822Date(d: Date): string {
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const pad = (n: number) => String(n).padStart(2, "0");
  // Toutes les dates sont en UTC pour simplifier (et c'est ce que les
  // serveurs SMTP attendent typiquement)
  return (
    `${days[d.getUTCDay()]}, ${pad(d.getUTCDate())} ${months[d.getUTCMonth()]} ` +
    `${d.getUTCFullYear()} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:` +
    `${pad(d.getUTCSeconds())} +0000`
  );
}

/** Strip HTML → text plain pour le fallback text/plain part. */
function stripHtmlToText(html: string): string {
  // DOMParser dispo dans le navigateur
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return (doc.body.textContent || "").trim();
  } catch {
    // Fallback regex simple
    return html.replace(/<[^>]+>/g, "").trim();
  }
}
